import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

interface EmptyStateProps {
  icon: React.ElementType;
  title: string;
  description?: string;
  buttonLabel?: string;
  buttonTo?: string;
  onButtonClick?: () => void;
  buttonIcon?: React.ElementType;
  /** 'default' = full area centering (flex-1 + min-h-[60vh]), 'sm' = compact for sections (py-8) */
  size?: 'default' | 'sm';
  /** Tailwind gradient classes for icon badge, e.g. 'from-accent-primary/20 to-accent-info/20 border-accent-primary/20 text-accent-primary' */
  iconColorClass?: string;
  /** Button variant - default 'primary'. 'secondary' dla lżejszych CTA w kartach. */
  buttonVariant?: 'primary' | 'secondary';
  /** Gdy rodzic jest `flex flex-col`, EmptyState wypełnia dostępną wysokość
   * i centruje content w pionie. Używane dla siatek kart 2x2 żeby puste karty
   * miały tę samą wysokość co pełne. */
  fillHeight?: boolean;
  /** Alternatywa dla buttonLabel - custom node renderowany w miejscu buttona
   * (np. dwa przyciski obok siebie dla Przesyłki "InPost Kurier / Paczkomat"
   * albo Faktury "Wystaw fakturę / Wystaw paragon"). */
  actions?: ReactNode;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  buttonLabel,
  buttonTo,
  onButtonClick,
  buttonIcon: ButtonIcon,
  size = 'default',
  iconColorClass = 'from-accent-primary/20 to-accent-info/20 border-accent-primary/20 text-accent-primary',
  buttonVariant = 'primary',
  fillHeight = false,
  actions,
}: EmptyStateProps) {
  const isDefault = size === 'default';
  // fillHeight=true: bez py-*, bo flex-1 + justify-center w kontenerze flex-col
  // już wyśrodkowuje content w pionie. Dodatkowy padding łamie symetrię, gdy
  // rodzic ma asymetryczny układ (h3+mb-4 u góry, pure padding u dołu).
  const containerClasses = isDefault
    ? 'flex-1 min-h-[60vh] px-6'
    : fillHeight
      ? 'flex-1 px-4 py-2'
      : 'py-8 px-4';

  const btnClass = buttonVariant === 'secondary' ? 'btn-secondary' : 'btn-primary';

  return (
    <div className={`flex flex-col items-center justify-center text-center ${containerClasses}`}>
      <div className={`rounded-2xl bg-gradient-to-br border flex items-center justify-center mx-auto ${iconColorClass} ${
        isDefault ? 'w-14 h-14 mb-4' : 'w-10 h-10 mb-3'
      }`}>
        <Icon className={isDefault ? 'w-7 h-7' : 'w-5 h-5'} />
      </div>
      <p className={`font-semibold ${isDefault ? 'text-text-primary text-lg mb-1.5' : 'text-text-primary text-base mb-1'}`}>
        {title}
      </p>
      {description && (
        <p className={`text-text-muted ${isDefault ? 'text-base max-w-md' : 'text-sm max-w-sm'}`}>
          {description}
        </p>
      )}
      {actions ? (
        <div className={isDefault ? 'mt-7' : 'mt-4'}>{actions}</div>
      ) : buttonLabel && (buttonTo || onButtonClick) ? (
        buttonTo ? (
          <Link to={buttonTo} className={`${btnClass} inline-flex items-center gap-1.5 ${isDefault ? 'text-sm mt-7' : 'text-xs mt-4'}`}>
            {ButtonIcon && <ButtonIcon className="w-3.5 h-3.5" />}
            {buttonLabel}
          </Link>
        ) : (
          <button onClick={onButtonClick} className={`${btnClass} inline-flex items-center gap-1.5 ${isDefault ? 'text-sm mt-7' : 'text-xs mt-4'}`}>
            {ButtonIcon && <ButtonIcon className="w-3.5 h-3.5" />}
            {buttonLabel}
          </button>
        )
      ) : null}
    </div>
  );
}
